import { DashboardInfoBox, Grid, TrendingUpIcon, TrendingDownIcon } from '@esgian/esgianui';
import React from 'react';
import PropTypes from 'prop-types';
import { TextWithTooltipIcon } from '@components';
import {
  formatToDecimalPlaces,
  formatNumberReadable,
  calculateDifferenceInPercentage
} from '@helpers';

const Summary = ({ esgianData, customData, activeMode }) => {
  const esgianSummary = {
    etsCost: formatToDecimalPlaces(esgianData?.emissionSummary?.etsCost, 2),
    co2e: formatToDecimalPlaces(esgianData?.emissionSummary?.co2eTn, 2),
    co2eIntensity: formatToDecimalPlaces(esgianData?.emissionSummary?.co2eIntensity, 4),
    co2: formatToDecimalPlaces(esgianData?.emissionSummary?.co2Tn, 2)
  };

  const customSummary = {
    etsCost: formatToDecimalPlaces(customData?.emissionSummary?.etsCost, 2),
    co2e: formatToDecimalPlaces(customData?.emissionSummary?.co2eTn, 2),
    co2eIntensity: formatToDecimalPlaces(customData?.emissionSummary?.co2eIntensity, 4),
    co2: formatToDecimalPlaces(customData?.emissionSummary?.co2Tn, 2)
  };

  const percentageInEtsCost =
    activeMode === 'esgian'
      ? calculateDifferenceInPercentage(
          esgianData?.emissionSummary?.etsCost,
          customData?.emissionSummary?.etsCost
        )
      : calculateDifferenceInPercentage(
          customData?.emissionSummary?.etsCost,
          esgianData?.emissionSummary?.etsCost
        );

  const percentageInCo2e =
    activeMode === 'esgian'
      ? calculateDifferenceInPercentage(
          esgianData?.emissionSummary?.co2eTn,
          customData?.emissionSummary?.co2eTn
        )
      : calculateDifferenceInPercentage(
          customData?.emissionSummary?.co2eTn,
          esgianData?.emissionSummary?.co2eTn
        );

  const percentageInCo2eIntensity =
    activeMode === 'esgian'
      ? calculateDifferenceInPercentage(
          esgianData?.emissionSummary?.co2eIntensity,
          customData?.emissionSummary?.co2eIntensity
        )
      : calculateDifferenceInPercentage(
          customData?.emissionSummary?.co2eIntensity,
          esgianData?.emissionSummary?.co2eIntensity
        );

  const percentageInCo2 =
    activeMode === 'esgian'
      ? calculateDifferenceInPercentage(
          esgianData?.emissionSummary?.co2Tn,
          customData?.emissionSummary?.co2Tn
        )
      : calculateDifferenceInPercentage(
          customData?.emissionSummary?.co2Tn,
          esgianData?.emissionSummary?.co2Tn
        );

  const infoBoxes = [
    {
      label: 'ETS cost(€)',
      subheader: 'For the voyage',
      value: formatNumberReadable(
        activeMode === 'esgian' ? esgianSummary?.etsCost : customSummary?.etsCost
      ),
      tooltip: 'Total EU ETS cost corresponding to the CO₂ emission for the voyage.',
      icon: !percentageInEtsCost ? null : percentageInEtsCost > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <TrendingUpIcon style={{ fontSize: '14px', color: 'red' }} />
          <span style={{ color: 'red', fontSize: '12px' }}>{percentageInEtsCost}%</span>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <TrendingDownIcon style={{ fontSize: '14px', color: 'green' }} />
          <span style={{ color: 'green', fontSize: '12px' }}>{percentageInEtsCost}%</span>
        </div>
      )
    },
    {
      label: 'CO₂e(t)',
      subheader: 'For the voyage',
      value: formatNumberReadable(
        activeMode === 'esgian' ? esgianSummary?.co2e : customSummary?.co2e
      ),
      tooltip:
        'Tonnes of CO₂e emission for the voyage. CO₂ equivalents (CO₂e) means the number of metric tons of CO₂ emissions with the same global warming potential as one metric ton of another greenhouse gas. As per ISO 14083 CO₂e Includes CO₂, CH4, CFCs,HFCs,NF3,N2O,PFCs,SF6,SO2F2',
      icon: !percentageInCo2e ? null : percentageInCo2e > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <TrendingUpIcon style={{ fontSize: '14px', color: 'red' }} />
          <span style={{ color: 'red', fontSize: '12px' }}>{percentageInCo2e}%</span>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <TrendingDownIcon style={{ fontSize: '14px', color: 'green' }} />
          <span style={{ color: 'green', fontSize: '12px' }}>{percentageInCo2e}%</span>
        </div>
      )
    },
    {
      label: 'CO₂e intensity(kgCO₂e/t.km)',
      subheader: 'For the voyage',
      value: activeMode === 'esgian' ? esgianSummary?.co2eIntensity : customSummary?.co2eIntensity,
      tooltip: 'CO₂e (kg) emitted per ton cargo weight carried per kilometer traveled.',
      icon: !percentageInCo2eIntensity ? null : percentageInCo2eIntensity > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <TrendingUpIcon style={{ fontSize: '14px', color: 'red' }} />
          <span style={{ color: 'red', fontSize: '12px' }}>{percentageInCo2eIntensity}%</span>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <TrendingDownIcon style={{ fontSize: '14px', color: 'green' }} />
          <span style={{ color: 'green', fontSize: '12px' }}>{percentageInCo2eIntensity}%</span>
        </div>
      )
    },
    {
      label: 'CO₂(t)',
      subheader: 'For the voyage',
      value:
        activeMode === 'esgian'
          ? formatNumberReadable(esgianSummary?.co2)
          : formatNumberReadable(customSummary?.co2),
      tooltip: 'Tonnes of Carbon Dioxide emitted for the voyage',
      icon: !percentageInCo2 ? null : percentageInCo2 > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <TrendingUpIcon style={{ fontSize: '14px', color: 'red' }} />
          <span style={{ color: 'red', fontSize: '12px' }}>{percentageInCo2}%</span>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <TrendingDownIcon style={{ fontSize: '14px', color: 'green' }} />
          <span style={{ color: 'green', fontSize: '12px' }}>{percentageInCo2}%</span>
        </div>
      )
    }
  ];

  return (
    <Grid container spacing={2} id="voyage-emission-kpis">
      {infoBoxes.map((infoBox) => (
        <Grid key={infoBox.label} item xs={3}>
          <DashboardInfoBox
            value={infoBox.value}
            header={
              <TextWithTooltipIcon
                labelBold
                labelVariant="subtitle1"
                label={infoBox.label}
                tooltipText={infoBox.tooltip}
              />
            }
            subheader={infoBox.subheader}
            icon={infoBox.icon}
          />
        </Grid>
      ))}
    </Grid>
  );
};

Summary.propTypes = {
  esgianData: PropTypes.object,
  customData: PropTypes.object,
  activeMode: PropTypes.string
};

export default Summary;
